<template>
  <div class="flex min-h-screen">
    <button class="admin-menu-toggle" @click="sidebarOpen = true" aria-label="Open menu">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
    </button>
    <div v-if="sidebarOpen" class="admin-sidebar-backdrop" @click="sidebarOpen = false"></div>
    <aside class="admin-sidebar" :class="{ 'admin-sidebar--open': sidebarOpen }">
      <div class="flex items-center justify-between pb-6 border-b border-black/6 mb-5">
        <div class="flex items-center gap-2.5 px-2">
          <img src="/static/images/unicorn.png" alt="Logo" width="32" class="rounded-full">
          <span class="font-bold text-[15px] text-ink -tracking-[0.01em]">Unicorn Visa</span>
        </div>
        <button class="admin-sidebar-close hidden max-md:flex items-center justify-center w-8 h-8 rounded-lg text-muted bg-transparent border-none cursor-pointer transition-colors duration-150 hover:bg-black/[0.04] hover:text-ink" @click="sidebarOpen = false" aria-label="Close menu">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      <nav class="flex flex-col gap-0.5 flex-1">
        <template v-if="user && user.isAdmin">
          <router-link
            :to="{ name: 'admin' }"
            class="admin-sidebar-link"
            :class="{ 'admin-sidebar-link--active': isDashboardRoute }">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="9"></rect><rect x="14" y="3" width="7" height="5"></rect><rect x="14" y="12" width="7" height="9"></rect><rect x="3" y="16" width="7" height="5"></rect></svg>
            <span class="flex-1">Dashboard</span>
          </router-link>
        </template>

        <span class="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted px-3.5 pb-1.5 mt-6">Cases</span>

        <router-link
          :to="{ name: 'admin-applicants' }"
          class="admin-sidebar-link"
          :class="{ 'admin-sidebar-link--active': isApplicantsRoute }">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
          <span class="flex-1">Applicants</span>
          <span v-if="applicantCount" class="text-xs font-semibold text-primary bg-[#f0eef5] px-[7px] py-px rounded-[10px] min-w-[22px] text-center">{{ applicantCount }}</span>
        </router-link>

        <template v-if="user && user.isAdmin">
          <span class="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted px-3.5 pb-1.5 mt-6">Admin</span>

          <router-link
            :to="{ name: 'admin-activities' }"
            class="admin-sidebar-link"
            :class="{ 'admin-sidebar-link--active': isActivitiesRoute }">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
            <span class="flex-1">Activity</span>
          </router-link>

          <router-link
            :to="{ name: 'admin-deadlines' }"
            class="admin-sidebar-link"
            :class="{ 'admin-sidebar-link--active': isDeadlinesRoute }">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
            <span class="flex-1">Deadlines</span>
          </router-link>

          <router-link
            :to="{ name: 'admin-bodies' }"
            class="admin-sidebar-link"
            :class="{ 'admin-sidebar-link--active': isBodiesRoute }">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
            <span class="flex-1">Bodies</span>
          </router-link>

          <router-link
            :to="{ name: 'admin-team' }"
            class="admin-sidebar-link"
            :class="{ 'admin-sidebar-link--active': isTeamRoute }">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
            <span class="flex-1">Team</span>
          </router-link>
        </template>
      </nav>

      <div class="border-t border-black/6 pt-4 flex flex-col gap-2">
        <div v-if="user" class="flex items-center gap-2.5 px-2">
          <img v-if="userPhoto" :src="userPhoto" :alt="user.firstName" class="w-7 h-7 rounded-full object-cover shrink-0">
          <div v-else class="w-7 h-7 rounded-full bg-[#f0eef5] text-primary flex items-center justify-center font-bold text-[11px] shrink-0">{{ userName ? userName.charAt(0) : '?' }}</div>
          <div class="flex flex-col min-w-0">
            <span class="text-[13px] font-semibold text-ink leading-tight truncate">{{ userName }}</span>
          </div>
        </div>
        <button type="button" class="flex items-center gap-2 p-2 text-[13px] text-muted bg-transparent border-0 cursor-pointer rounded-lg transition-colors duration-150 hover:text-ink w-full" @click="logout">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
          Sign out
        </button>
      </div>
    </aside>

    <main class="flex-1 ml-[220px] py-9 px-10 min-w-0 max-md:ml-0 max-md:px-4 max-md:py-5 max-md:pt-[68px]">
      <transition name="fade" mode="out-in">
        <router-view></router-view>
      </transition>
    </main>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { PHOTOS } from '@/services/photos'

export default {
  name: 'admin',
  data () {
    return {
      sidebarOpen: false
    }
  },
  watch: {
    '$route' () {
      this.sidebarOpen = false
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      applicants: state => state.applicants.applicants
    }),
    userName () {
      if (!this.user) return ''
      return this.user.firstName || (this.user.name ? this.user.name.split(' ')[0] : '') || this.user.email || ''
    },
    userPhoto () {
      if (!this.user) return null
      if (this.user.photoUrl) return this.user.photoUrl
      const name = this.userName
      if (!name) return null
      return PHOTOS[name.toLowerCase()] || null
    },
    applicantCount () {
      if (!this.applicants) return 0
      return this.applicants.filter(a => a.isActive).length
    },
    isApplicantsRoute () {
      return this.$route.name && (this.$route.name === 'admin-applicants' || this.$route.name === 'admin-applicant')
    },
    isDashboardRoute () {
      return this.$route.name === 'admin'
    },
    isActivitiesRoute () {
      return this.$route.name === 'admin-activities'
    },
    isBodiesRoute () {
      return this.$route.name && (this.$route.name === 'admin-bodies' || this.$route.name === 'admin-body')
    },
    isDeadlinesRoute () {
      return this.$route.name === 'admin-deadlines'
    },
    isTeamRoute () {
      return this.$route.name === 'admin-team'
    }
  },
  methods: {
    logout () {
      this.$store.dispatch('LOGOUT')
    }
  },
  created () {
    this.$store.dispatch('GET_USER')
    this.$store.dispatch('GET_APPLICANTS').catch(() => {})

    if ((this.user && !this.user.isAdmin) && (this.user && !this.user.isManager)) {
      this.$router.push({ name: 'components' })
    }
  }
}
</script>

<style scoped>
.admin-sidebar {
  width: 220px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 100;
}

.admin-sidebar-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #7D7480;
  text-decoration: none;
  border-radius: 12px;
  transition: color 0.15s, background 0.15s;
}

.admin-sidebar-link:hover {
  color: #131415;
  background: #f4f4f8;
  text-decoration: none;
}

.admin-sidebar-link--active {
  color: #7c5bea;
  background: rgba(124, 91, 234, 0.08);
  font-weight: 600;
}

.admin-sidebar-link--active:hover {
  color: #7c5bea;
  background: rgba(124, 91, 234, 0.1);
}

.admin-menu-toggle {
  display: none;
}

.admin-sidebar-backdrop {
  display: none;
}

@media (max-width: 768px) {
  .admin-menu-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 16px;
    left: 16px;
    z-index: 101;
    width: 40px;
    height: 40px;
    background: #fff;
    border: 1px solid #e2e4ed;
    border-radius: 12px;
    color: #131415;
    cursor: pointer;
    transition: border-color 0.15s;
  }

  .admin-menu-toggle:hover {
    border-color: #d6d8e3;
  }

  .admin-sidebar {
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    box-shadow: none;
  }

  .admin-sidebar--open {
    transform: translateX(0);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);
  }

  .admin-sidebar-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 99;
    background: rgba(0, 0, 0, 0.2);
  }

  .admin-sidebar-close {
    display: flex !important;
  }
}
</style>
